import { OidcProvider } from "@axa-fr/react-oidc";
import { useDispatch } from "react-redux";
import { logCustomMessage } from "../../../store/ui/uiActions";
import { AuthenticatingComponent } from "./AuthenticatingComponent";
import { CallbackSuccessComponent } from "./CallbackSuccessComponent";
import { LoadingComponent } from "./LoadingComponent";
import { ServiceWorkerNotSupportedComponent } from "./ServiceWorkerNotSupportedComponent";
import { SessionLostComponent } from "./SessionLostComponent";
import {
  Stored,
  canAttemptRecoveryNow,
  clearRecoveryThrottle,
  getClient,
  identityServerConfiguration,
  isSwTimeoutEvent,
  registerOidcWorker,
} from "./customOidcUtils";

const stringifyData = (d) => {
  if (d == null || d == undefined) return "null";

  if (typeof d === "string") return d;

  if (d instanceof Error) {
    return `${d.name}: ${d.message}\n${d.stack ?? ""}`;
  }

  try {
    return JSON.stringify(d, Object.getOwnPropertyNames(d));
  } catch {
    return String(d);
  }
};

const unregisterServiceWorkers = async () => {
  const regs = await (navigator.serviceWorker?.getRegistrations?.() || []);
  await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
};

const registerOidcServiceWorker = async () => {
  const reg = await registerOidcWorker();
  await navigator.serviceWorker.ready; // waits for an active worker
  return reg;
};

const recoverSilently = async () => {
  try {
    await unregisterServiceWorkers();
    const reg = await registerOidcServiceWorker();
    !navigator.serviceWorker.controller &&
      reg.active?.postMessage({ type: "claim" });

    const oidc = getClient();
    await oidc.loginAsync(`${process.env.REACT_APP_URI}/`, {
      prompt: "none",
      timestamp: Date.now(),
    });
    return true;
  } catch (e) {
    console.log("[recover] error", e);
    return false;
  }
};

const hardLogout = async () => {
  try {
    await getClient().logoutAsync();
  } catch (e) {
    console.log("[recover] hardLogout error", e);
    window.location.assign(`${process.env.REACT_APP_URI}/logout`);
  }
};

// This custom version of OidcProvider refreshes logged out pages when a login event occurs
export const CustomOidcProvider = (props) => {
  console.log("CustomOidcProvider component rendered");
  const dispatch = useDispatch();
  if (!localStorage["oidc.login.default"]) {
    console.log("Oidc profile not found in local storage, creating new entry");
    localStorage["oidc.login.default"] = JSON.stringify({
      callbackPath: `${process.env.REACT_APP_URI}/`,
      extras: null,
    });
  }

  return (
    <OidcProvider
      serviceWorkerNotSupportedComponent={ServiceWorkerNotSupportedComponent}
      loadingComponent={LoadingComponent}
      callbackSuccessComponent={CallbackSuccessComponent}
      authenticatingComponent={AuthenticatingComponent}
      authenticatingErrorComponent={() => (
        <SessionLostComponent isAuthenticationError={true} />
      )}
      sessionLostComponent={SessionLostComponent}
      configuration={identityServerConfiguration}
      onEvent={async (_, name, data) => {
        if (name === "loginCallbackAsync_end") {
          const currentTimestamp = Math.floor(Date.now() / 1000);
          localStorage.setItem(
            Stored.LAST_LOGIN_TIMESTAMP,
            currentTimestamp.toString()
          );
          console.log(
            "User logged in successfully. Timestamp:",
            currentTimestamp
          );
          clearRecoveryThrottle();
        }
        if (
          name === "loginCallbackAsync_error" ||
          name === "loginAsync_error" ||
          name === "refreshTokensAsync_silent_error" ||
          name === "silentLoginAsync_error" ||
          name === "tokensInvalidAndWaitingActionsToRefresh"
        ) {
          console.log(name, data);
        }

        if (isSwTimeoutEvent(name, data) && canAttemptRecoveryNow()) {
          console.log("[recover] event", name);
          const ok = await recoverSilently();
          if (!ok) {
            console.log("[recover] failed → hardLogout()");
            await hardLogout();
          }
        }
      }}
    >
      {props.children}
    </OidcProvider>
  );
};
